import { useCallback } from "react"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import { refusalOf } from "features/TabSpec/api/usePreparationSources"
import { runOnLoadRefusal } from "features/TabSpec/runtime/preparation"
import {
  parseRunOnLoadList,
  RUN_ON_LOAD_FILE,
  serializeRunOnLoadList,
} from "types/runOnLoad.types"
import type { RunOnLoadMeta } from "types/savedQuery.types"

const META: RunOnLoadMeta = { kind: "runonload" }

/** The list could not be listed or fetched: it may be there, so a save must not replace it unseen. */
export const LIST_NOT_CHECKED = "The saved list could not be checked just now."
/** The list was read but does not parse: a save replaces it. */
export const LIST_NOT_READ = "The saved list could not be read. Saving replaces it."
/** What `check` says of a saved pipeline or builder. */
export const NOT_A_SCRIPT = "It is a pipeline or builder: only saved scripts run on load."
/** What `check` says when the saved records' kinds could not be read. */
export const KIND_NOT_CHECKED = "It could not be checked just now. Try again."

/** Why `read` could not give the list; `replaceable` when it was read but does not parse. */
export class RunOnLoadListError extends Error {
  readonly replaceable: boolean

  constructor(replaceable: boolean) {
    super(replaceable ? LIST_NOT_READ : LIST_NOT_CHECKED)
    this.name = "RunOnLoadListError"
    this.replaceable = replaceable
  }
}

/**
 * Reads and saves the project's Run on load list, and checks a script before
 * it goes on it. The list takes saved scripts only: a pipeline or builder is
 * re-run after RESET wherever it is bound, so on the list it would fail with
 * "field exists". A name without a sidecar is a script, as in the console.
 */
export const useRunOnLoadList = (projectId: string, owner: string) => {
  const { getProjectQueries, getQuery, saveStructured, listStructured } = useSavedQueries()

  /**
   * The list; [] when the project has none. Throws a RunOnLoadListError when
   * the listing or the file read fails or is refused (not replaceable), or when
   * the file was read but does not parse (replaceable).
   */
  const read = useCallback(async (): Promise<string[]> => {
    let listed: unknown
    try {
      listed = (await getProjectQueries(projectId, owner)).data
    } catch {
      throw new RunOnLoadListError(false)
    }
    if (refusalOf(listed) !== null) throw new RunOnLoadListError(false)
    const names = String(listed ?? "").split("\n").map((n) => n.trim())
    if (!names.includes(RUN_ON_LOAD_FILE)) return []
    let body: unknown
    try {
      body = (await getQuery(projectId, owner, RUN_ON_LOAD_FILE)).data
    } catch {
      throw new RunOnLoadListError(false)
    }
    if (refusalOf(body) !== null) throw new RunOnLoadListError(false)
    const list = parseRunOnLoadList(body)
    if (!list) throw new RunOnLoadListError(true)
    return list
  }, [getProjectQueries, getQuery, projectId, owner])

  /**
   * Of `names`, the saved scripts, in order: a name whose sidecar says
   * another kind is left out. Throws when the kinds cannot be read.
   */
  const scriptsOf = useCallback(
    async (names: string[]): Promise<string[]> => {
      const kinds = new Map((await listStructured(projectId, owner)).map((e) => [e.name, e.kind]))
      return names.filter((n) => (kinds.get(n) ?? "script") === "script")
    },
    [listStructured, projectId, owner]
  )

  const save = useCallback(
    async (scripts: string[]): Promise<void> => {
      await saveStructured(projectId, owner, RUN_ON_LOAD_FILE, serializeRunOnLoadList(scripts), META)
    },
    [saveStructured, projectId, owner]
  )

  /** Why saved query `name` cannot go on the list, or null; its kind and body are read afresh. */
  const check = useCallback(
    async (name: string): Promise<string | null> => {
      try {
        if ((await scriptsOf([name])).length === 0) return NOT_A_SCRIPT
      } catch {
        return KIND_NOT_CHECKED
      }
      let body: unknown = ""
      try {
        body = (await getQuery(projectId, owner, name)).data
      } catch {
        body = ""
      }
      // A refusal in place of the body reads as a body that could not be read.
      return runOnLoadRefusal(typeof body === "string" && refusalOf(body) === null ? body : "")
    },
    [scriptsOf, getQuery, projectId, owner]
  )

  return { read, save, check, scriptsOf }
}
